import { useRef } from "react";
import { motion } from "framer-motion";
import { Boxes, Sparkles, Cloud, Workflow, Wrench, Server, Layout, Rocket } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const ITEMS = [
    { icon: Boxes, num: "01", title: "Product Development", body: "From concept to shipped product. Strategy, architecture, and engineering as one motion.", featured: true },
    { icon: Sparkles, num: "02", title: "AI Integrations", body: "LLM workflows, retrieval systems, intelligent automation woven into real products." },
    { icon: Cloud, num: "03", title: "SaaS Platforms", body: "Multi-tenant systems with thoughtful billing, auth, and operational ergonomics." },
    { icon: Workflow, num: "04", title: "Automation Systems", body: "Reliable pipelines and orchestration that quietly remove human toil." },
    { icon: Wrench, num: "05", title: "Internal Tools", body: "Bespoke tools that give small teams the leverage of much larger ones." },
    { icon: Server, num: "06", title: "API & Backend", body: "Composable APIs, strong primitives, observable systems built to last." },
    { icon: Layout, num: "07", title: "UX-focused Applications", body: "Interfaces that feel quiet, considered, and impossibly easy to use." },
    { icon: Rocket, num: "08", title: "MVP Development", body: "Get to a real product fast, without the technical debt that haunts later stages." },
];

export default function Capabilities() {
    return (
        <section id="capabilities" data-testid="capabilities-section" className="relative py-32 md:py-48">
            <div className="mx-auto max-w-[1480px] px-6 sm:px-12 md:px-20">
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-15%" }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="mb-16 flex items-baseline gap-6 border-b border-sb pb-6"
                >
                    <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-sb-mint">02 · Practice</span>
                    <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.24em] text-sb-muted">/ Eight disciplines</span>
                    <span className="ml-auto hidden md:inline font-mono text-[10px] uppercase tracking-[0.24em] text-sb-muted">What we make</span>
                </motion.div>

                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 0.8, ease: EASE, delay: 0.05 }}
                    className="balance max-w-[24ch] text-[clamp(1.875rem,3.8vw,3.5rem)] font-medium leading-[1.08] tracking-[-0.025em] mb-16"
                >
                    Eight disciplines.{" "}
                    <span className="font-serif italic text-sb-mint">One unified</span> engineering practice.
                </motion.h2>

                {/* Bento grid */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    {/* Featured large card */}
                    <FeaturedCard item={ITEMS[0]} className="md:col-span-7 md:row-span-2" />

                    {/* Top right pair */}
                    <SmallCard item={ITEMS[1]} className="md:col-span-5" />
                    <SmallCard item={ITEMS[2]} className="md:col-span-5" />

                    {/* Second row */}
                    <SmallCard item={ITEMS[3]} className="md:col-span-4" />
                    <SmallCard item={ITEMS[4]} className="md:col-span-4" />
                    <SmallCard item={ITEMS[5]} className="md:col-span-4" />

                    {/* Third row */}
                    <SmallCard item={ITEMS[6]} className="md:col-span-6" />
                    <SmallCard item={ITEMS[7]} className="md:col-span-6" />
                </div>
            </div>
        </section>
    );
}

function FeaturedCard({ item, className = "" }) {
    const ref = useRef(null);
    const onMove = (e) => {
        const r = ref.current; if (!r) return;
        const rect = r.getBoundingClientRect();
        r.style.setProperty("--mx", `${e.clientX - rect.left}px`);
        r.style.setProperty("--my", `${e.clientY - rect.top}px`);
    };
    const Icon = item.icon;
    return (
        <motion.article
            ref={ref}
            onMouseMove={onMove}
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.7, ease: EASE }}
            data-testid="capability-card-0"
            className={`cap-card group relative overflow-hidden rounded-xl border-sb border bg-sb-2 p-8 md:p-12 ${className}`}
        >
            <div className="gleam" />
            {/* Decorative arch grid */}
            <div className="absolute inset-0 bg-arch-grid opacity-50" aria-hidden />
            <div className="pointer-events-none absolute -right-20 -bottom-20 h-72 w-72 mint-glow opacity-50" aria-hidden />

            <div className="relative z-10 flex h-full flex-col justify-between gap-12">
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-md border-sb border bg-sb-surface/60">
                            <Icon className="h-4 w-4 text-sb-mint" strokeWidth={1.5} />
                        </div>
                        <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-sb-muted">
                            {item.num} · Flagship
                        </span>
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-sb-muted">
                        /Featured
                    </span>
                </div>

                <div className="space-y-6">
                    <h3 className="balance text-[clamp(1.5rem,2.4vw,2.25rem)] font-medium leading-[1.1] tracking-[-0.02em]">
                        Product <span className="font-serif italic text-sb-mint">Development</span>
                    </h3>
                    <p className="max-w-[46ch] text-sm md:text-base text-sb-soft font-light leading-relaxed">
                        From the first sketch to a shipped product. Strategy, architecture,
                        and engineering as one continuous motion. We design the seams as
                        carefully as the surfaces.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {["Discovery", "Architecture", "Engineering", "Launch"].map((t) => (
                            <span key={t} className="rounded-full border-sb border bg-sb-surface/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-sb-soft">
                                {t}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </motion.article>
    );
}

function SmallCard({ item, className = "" }) {
    const ref = useRef(null);
    const onMove = (e) => {
        const r = ref.current; if (!r) return;
        const rect = r.getBoundingClientRect();
        r.style.setProperty("--mx", `${e.clientX - rect.left}px`);
        r.style.setProperty("--my", `${e.clientY - rect.top}px`);
    };
    const Icon = item.icon;
    return (
        <motion.article
            ref={ref}
            onMouseMove={onMove}
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.6, ease: EASE, delay: parseInt(item.num) * 0.04 }}
            data-testid={`capability-card-${parseInt(item.num) - 1}`}
            className={`cap-card group relative overflow-hidden rounded-xl border-sb border bg-sb-2 p-6 md:p-8 transition-colors hover:border-sb-mint/30 ${className}`}
        >
            <div className="gleam" />
            <div className="relative z-10 flex h-full flex-col gap-5">
                <div className="flex items-center justify-between">
                    <div className="flex h-9 w-9 items-center justify-center rounded-md border-sb border transition-colors group-hover:bg-sb-mint">
                        <Icon className="h-3.5 w-3.5 text-sb-soft group-hover:text-[#0B1220]" strokeWidth={1.6} />
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-sb-muted">{item.num}</span>
                </div>
                <h3 className="text-base md:text-lg font-medium tracking-tight text-sb">{item.title}</h3>
                <p className="text-[13px] text-sb-muted font-light leading-relaxed">{item.body}</p>
            </div>
        </motion.article>
    );
}
